import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cur } from '@/lib/currency';

const CATS = [
  [/munic|baladi|balady|بلدي|amana|امانة/i, 'Municipality Fines'],
  [/traffic|muroor|moroor|مرور|speed|saher|ساهر|parking/i, 'Traffic Fines'],
  [/civil defen|دفاع|labou?r|mol|gosi|zakat|zatca|passport|jawazat|health|sfda|government|gov/i, 'Other Government Fines'],
  [/hr|misconduct|internal|employee/i, 'Internal / Employee Fines'],
];
export const fineCategory = (f) => {
  const txt = `${f.fine_type || ''} ${f.department || ''} ${f.description || ''}`;
  const hit = CATS.find(([re]) => re.test(txt));
  return hit ? hit[1] : 'Other Fines';
};
const n2 = (v) => Number(v || 0).toFixed(2);
const today = new Date().toISOString().slice(0, 10);

function summarize(list) {
  const amount = list.reduce((s, f) => s + Number(f.amount || 0), 0);
  const paid = list.reduce((s, f) => s + Number(f.paid_amount || 0), 0);
  const overdue = list.filter((f) => f.due_date && String(f.due_date).slice(0, 10) < today && f.payment_status !== 'paid')
    .reduce((s, f) => s + Number(f.amount || 0) - Number(f.paid_amount || 0), 0);
  const status = paid <= 0.004 ? 'unpaid' : paid >= amount - 0.004 ? 'paid' : 'partial';
  return { count: list.length, amount, paid, remaining: amount - paid, overdue, status };
}

const STATUS_CLS = { paid: 'bg-success/20 text-success', partial: 'bg-warning/20 text-warning', unpaid: 'bg-error/20 text-error' };

function SumCells({ s }) {
  return (
    <>
      <td className="p-3 text-sm text-right">{s.count}</td>
      <td className="p-3 text-sm text-right font-bold">{cur()} {n2(s.amount)}</td>
      <td className="p-3 text-sm text-right text-success">{cur()} {n2(s.paid)}</td>
      <td className="p-3 text-sm text-right text-warning">{cur()} {n2(s.remaining)}</td>
      <td className="p-3 text-sm text-right text-error">{s.overdue ? `${cur()} ${n2(s.overdue)}` : '—'}</td>
      <td className="p-3 text-center"><Badge className={STATUS_CLS[s.status]}>{s.status}</Badge></td>
    </>
  );
}

// Fines grouped branch → category; each fine counted once under its responsible branch.
export function FinesBranchGroups({ fines, branches, renderRow }) {
  const [open, setOpen] = useState({});
  const name = Object.fromEntries((branches || []).map((b) => [b.id, b.name]));
  const byBranch = {};
  fines.forEach((f) => {
    const b = f.branch_id || 'none';
    ((byBranch[b] ||= {})[fineCategory(f)] ||= []).push(f);
  });
  const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));
  const keys = Object.keys(byBranch).sort((a, b) => (name[a] || 'zz').localeCompare(name[b] || 'zz'));
  return (
    <div className="overflow-x-auto"><table className="w-full" data-testid="fines-grouped-table"><thead><tr className="border-b">
      <th className="text-left p-3 text-sm font-medium">Branch / category</th><th className="text-right p-3 text-sm font-medium">Fines</th>
      <th className="text-right p-3 text-sm font-medium">Amount</th><th className="text-right p-3 text-sm font-medium">Paid</th>
      <th className="text-right p-3 text-sm font-medium">Remaining</th><th className="text-right p-3 text-sm font-medium">Overdue</th>
      <th className="text-center p-3 text-sm font-medium">Status</th>
    </tr></thead><tbody>
      {keys.map((b) => {
        const cats = byBranch[b];
        return (
          <Fragment key={b}>
            <tr className="border-b bg-muted/40 cursor-pointer" onClick={() => toggle(b)} data-testid={`fines-branch-${b}`}>
              <td className="p-3 text-sm font-semibold">{open[b] ? <ChevronDown size={14} className="inline mr-1" /> : <ChevronRight size={14} className="inline mr-1" />}{name[b] || 'No branch'}</td>
              <SumCells s={summarize(Object.values(cats).flat())} />
            </tr>
            {open[b] && Object.keys(cats).sort().map((c) => (
              <Fragment key={c}>
                <tr className="border-b cursor-pointer hover:bg-muted/30" onClick={() => toggle(`${b}|${c}`)} data-testid={`fines-cat-${b}-${c}`}>
                  <td className="p-3 pl-8 text-sm">{open[`${b}|${c}`] ? <ChevronDown size={12} className="inline mr-1" /> : <ChevronRight size={12} className="inline mr-1" />}{c}</td>
                  <SumCells s={summarize(cats[c])} />
                </tr>
                {open[`${b}|${c}`] && (
                  <tr className="border-b"><td colSpan={7} className="p-0 pl-8">
                    <table className="w-full"><tbody>{cats[c].map((f) => (
                      <Fragment key={f.id}>
                        {renderRow(f)}
                        {f.payments?.length > 0 && (
                          <tr data-testid={`fine-payments-${f.id}`}><td colSpan={9} className="px-3 pb-2 text-[11px] text-muted-foreground">
                            Payments: {f.payments.map((p) => `${String(p.at).slice(0, 10)} ${cur()} ${n2(p.amount)} (${p.mode}${p.paying_branch_id && p.paying_branch_id !== f.branch_id ? `, paid by ${name[p.paying_branch_id] || 'other branch'}` : ''})`).join(' · ')}
                          </td></tr>
                        )}
                      </Fragment>
                    ))}</tbody></table>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </Fragment>
        );
      })}
      {keys.length > 0 && (
        <tr className="border-t-2 font-semibold" data-testid="fines-grouped-total"><td className="p-3 text-sm">All branches</td><SumCells s={summarize(fines)} /></tr>
      )}
      {keys.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No fines recorded</td></tr>}
    </tbody></table></div>
  );
}
